import type { VoidComponent } from "solid-js";
import { createEffect } from "solid-js";
import pMinDelay from "p-min-delay";
import DetailModal from "../../../../../components/modal/DetailModal";
import { useTranslation } from "../../../../../state/i18n";
import { useCurrentModal, useModalManager } from "../../../../../components/modal/ModalManager";
import { createForm } from "../../../../../components/form/createForm";
import { Form } from "../../../../../components/form/Form";
import { MINIMUM_ACTION_LOADING_DELAY } from "../../../../../constants";
import { TextButton } from "../../../../../components/button/Button";
import TerminationFeedbackFields, {
  type TerminationFeedback,
  terminationFeedbackSchema,
} from "../../components/TerminationFeedbackFields";
import BillingService from "../../../../../services/BillingService";
import GenericFailureModal from "../../../../GenericFailureModal";
import SubscriptionCancelledModal from "./SubscriptionCancelledModal";

type ConfirmCancellationModalProps = {
  subscriptionId: string;
  cycleEnd?: number;
};

const ConfirmCancellationModal: VoidComponent<ConfirmCancellationModalProps> = (props) => {
  const { t } = useTranslation();
  const { open, replace } = useModalManager();
  const { changeCloseBehaviour } = useCurrentModal();
  const cancelSubscription = BillingService.cancelSubscription();

  const form = createForm<TerminationFeedback>(
    terminationFeedbackSchema,
    async ({ terminationReasons, message }) => {
      await pMinDelay(
        cancelSubscription.mutateAsync({ subscription: props.subscriptionId, terminationReasons, message }),
        MINIMUM_ACTION_LOADING_DELAY,
      )
        .then(() => replace({ content: () => <SubscriptionCancelledModal cycleEnd={props.cycleEnd} /> }))
        .catch((error: unknown) => open({ content: () => <GenericFailureModal error={error} /> }));
    },
    {
      terminationReasons: [],
      message: undefined,
    },
  );

  createEffect(() => {
    changeCloseBehaviour(form.isSubmitting() ? "off" : "default");
  });

  return (
    <DetailModal
      title={t(($) => $.CONFIRM_CANCEL_MODAL_TITLE)}
      content={() => (
        <Form form={form}>
          {({ updateValue, error }) => <TerminationFeedbackFields updateValue={updateValue} error={error} />}
        </Form>
      )}
      fullHeight
      footer={
        <div class="flex w-full justify-end gap-2">
          <TextButton
            text={t(($) => $.CONFIRM_CANCEL_MODAL_CONFIRM_TITLE)}
            variant="primary"
            color="destructive"
            disabled={form.submitDisabled()}
            loading={form.isSubmitting()}
            onClick={() => {
              void form.submit();
            }}
          />
        </div>
      }
    />
  );
};

export default ConfirmCancellationModal;
